// src/utils/addressSchema.js
// Shared address validation schema for checkout
// Used by both client-side forms and can be mirrored server-side

/**
 * Pattern to detect 3+ consecutive non-alphanumeric symbols
 * A "symbol" is any character that is NOT [A-Za-z0-9 ] (letters, digits, or space)
 * This helps reject spam/gibberish while allowing normal punctuation
 */
const CONSECUTIVE_SYMBOLS_PATTERN = /[^A-Za-z0-9\s]{3,}/;

/**
 * Validation rules and patterns for address fields
 */
export const AddressRules = {
  firstName: {
    required: true,
    minLength: 1,
    maxLength: 80,
    pattern: /^[a-zA-Z\s\-'.]+$/,
    errorMessages: {
      required: 'First name is required',
      minLength: 'First name must be at least 1 character',
      maxLength: 'First name must be 80 characters or less',
      pattern: 'First name can only contain letters, spaces, hyphens, apostrophes, and periods',
    },
  },
  lastName: {
    required: true,
    minLength: 1,
    maxLength: 80,
    pattern: /^[a-zA-Z\s\-'.]+$/,
    errorMessages: {
      required: 'Last name is required',
      minLength: 'Last name must be at least 1 character',
      maxLength: 'Last name must be 80 characters or less',
      pattern: 'Last name can only contain letters, spaces, hyphens, apostrophes, and periods',
    },
  },
  company: {
    required: false,
    minLength: 0,
    maxLength: 80,
    pattern: /^[a-zA-Z0-9\s\-'.&,()]+$/,
    errorMessages: {
      maxLength: 'Company name must be 80 characters or less',
      pattern: 'Company name contains invalid characters',
    },
  },
  addressLine1: {
    required: true,
    minLength: 1,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s.,#\-/&']+$/,
    consecutiveSymbolsPattern: CONSECUTIVE_SYMBOLS_PATTERN,
    errorMessages: {
      required: 'Street address is required',
      minLength: 'Street address must be at least 1 character',
      maxLength: 'Street address must be 100 characters or less',
      pattern: 'Street address can only contain letters, numbers, and common punctuation (.,#-/&\')',
      consecutiveSymbols: 'That doesn\'t look like a valid address. Please make sure the address you entered is correct.',
    },
  },
  addressLine2: {
    required: false,
    minLength: 0,
    maxLength: 100,
    pattern: /^[a-zA-Z0-9\s.,#\-/&']*$/,
    consecutiveSymbolsPattern: CONSECUTIVE_SYMBOLS_PATTERN,
    errorMessages: {
      maxLength: 'Address line 2 must be 100 characters or less',
      pattern: 'Address line 2 contains invalid characters',
      consecutiveSymbols: 'That doesn\'t look like a valid address. Please make sure the address you entered is correct.',
    },
  },
  city: {
    required: true,
    minLength: 2,
    maxLength: 60,
    pattern: /^[a-zA-Z\s\-']+$/,
    consecutiveSymbolsPattern: CONSECUTIVE_SYMBOLS_PATTERN,
    errorMessages: {
      required: 'City is required',
      minLength: 'City must be at least 2 characters',
      maxLength: 'City must be 60 characters or less',
      pattern: 'City can only contain letters, spaces, hyphens, and apostrophes',
      consecutiveSymbols: 'That doesn\'t look like a valid address. Please make sure the address you entered is correct.',
    },
  },
  state: {
    required: true,
    errorMessages: {
      required: 'State is required',
      invalid: 'Please select a valid state',
    },
  },
  zip: {
    required: true,
    minLength: 5,
    maxLength: 10, // Allow 9-digit ZIP+4 with hyphen
    pattern: /^\d{5}(-\d{4})?$/,
    errorMessages: {
      required: 'ZIP code is required',
      minLength: 'ZIP code must be at least 5 digits',
      maxLength: 'ZIP code is too long',
      pattern: 'ZIP code must be 5 digits (e.g., 64105) or 9 digits (e.g., 64105-1234)',
    },
  },
  phone: {
    required: true,
    minLength: 10, // After digit extraction
    maxLength: 20,
    pattern: /^[\d\s()\-+]+$/,
    errorMessages: {
      required: 'Phone number is required',
      minLength: 'Phone number must be at least 10 digits',
      maxLength: 'Phone number is too long',
      pattern: 'Phone number can only contain digits, spaces, parentheses, hyphens, and plus sign',
    },
  },
};

/**
 * Normalize text by trimming and collapsing multiple spaces
 */
export function normalizeText(text) {
  if (!text || typeof text !== 'string') return '';
  // Trim, collapse multiple spaces, remove emoji and control chars
  return text
    .trim()
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoji: emoticons
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Emoji: misc symbols
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Emoji: transport
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // Emoji: flags
    .replace(/[\u{2600}-\u{26FF}]/gu, '') // Emoji: misc symbols
    .replace(/[\u{2700}-\u{27BF}]/gu, '') // Emoji: dingbats
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '') // Variation selectors
    .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Emoji: supplemental symbols
    .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Emoji: extended-A
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ''); // Control characters
}

/**
 * Format ZIP code to 5-digit display (strips non-digits, takes first 5)
 */
export function formatZip(zip) {
  if (!zip) return '';
  const digits = zip.replace(/\D/g, '');
  return digits.slice(0, 5);
}

/**
 * Format phone to (XXX) XXX-XXXX display
 */
export function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Extract digits only from phone (for validation)
 */
export function extractPhoneDigits(phone) {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Title-case city names (preserving all-caps acronyms like "LA")
 */
export function titleCaseCity(city) {
  if (!city) return '';
  return city
    .split(' ')
    .map(word => {
      // Preserve all-caps acronyms (2-3 chars, all uppercase)
      if (word.length <= 3 && word === word.toUpperCase()) {
        return word;
      }
      // Title case everything else
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Validate a single field value against its rules
 * Returns: { valid: boolean, error: string | null }
 */
export function validateField(fieldName, value, rules) {
  const rule = rules[fieldName];
  if (!rule) return { valid: true, error: null };

  // Normalize value
  let normalizedValue = value;
  if (typeof value === 'string') {
    normalizedValue = normalizeText(value);
  }

  // Required check
  if (rule.required && (!normalizedValue || normalizedValue.length === 0)) {
    return { valid: false, error: rule.errorMessages.required };
  }

  // Skip further validation if field is optional and empty
  if (!rule.required && (!normalizedValue || normalizedValue.length === 0)) {
    return { valid: true, error: null };
  }

  // Min length
  if (rule.minLength && normalizedValue.length < rule.minLength) {
    return { valid: false, error: rule.errorMessages.minLength };
  }

  // Max length
  if (rule.maxLength && normalizedValue.length > rule.maxLength) {
    return { valid: false, error: rule.errorMessages.maxLength };
  }

  // Consecutive symbols check (for address fields)
  if (rule.consecutiveSymbolsPattern && rule.consecutiveSymbolsPattern.test(normalizedValue)) {
    return { valid: false, error: rule.errorMessages.consecutiveSymbols };
  }

  // Pattern match
  if (rule.pattern && !rule.pattern.test(normalizedValue)) {
    return { valid: false, error: rule.errorMessages.pattern };
  }

  return { valid: true, error: null };
}

/**
 * Validate entire address object
 * Returns: { valid: boolean, errors: { [field]: string } }
 */
export function validateAddress(address) {
  const errors = {};
  let isValid = true;

  // Map form field names to rule keys
  const fieldMapping = {
    first_name: 'firstName',
    last_name: 'lastName',
    company: 'company',
    address_line1: 'addressLine1',
    address_line2: 'addressLine2',
    city: 'city',
    state: 'state',
    zip: 'zip',
    phone: 'phone',
  };

  Object.entries(fieldMapping).forEach(([formField, ruleKey]) => {
    const value = address[formField];
    const result = validateField(ruleKey, value, AddressRules);

    if (!result.valid) {
      errors[formField] = result.error;
      isValid = false;
    }
  });

  // Special validation for phone (check digit count after extraction)
  if (address.phone) {
    const digits = extractPhoneDigits(address.phone);
    if (digits.length < 10) {
      errors.phone = AddressRules.phone.errorMessages.minLength;
      isValid = false;
    } else if (digits.length > 15) {
      errors.phone = AddressRules.phone.errorMessages.maxLength;
      isValid = false;
    }
  }

  // Special validation for ZIP (must be 5 or 9 digits after extraction)
  if (address.zip) {
    const zipDigits = address.zip.replace(/\D/g, '');
    if (zipDigits.length !== 5 && zipDigits.length !== 9) {
      errors.zip = AddressRules.zip.errorMessages.pattern;
      isValid = false;
    }
  }

  return { valid: isValid, errors };
}

/**
 * Normalize address object (trim, collapse spaces, format special fields)
 * Returns new object with normalized values
 */
export function normalizeAddress(address) {
  return {
    first_name: normalizeText(address.first_name || ''),
    last_name: normalizeText(address.last_name || ''),
    company: normalizeText(address.company || ''),
    address_line1: normalizeText(address.address_line1 || ''),
    address_line2: normalizeText(address.address_line2 || ''),
    city: titleCaseCity(normalizeText(address.city || '')),
    state: address.state || '',
    zip: formatZip(address.zip || ''),
    phone: extractPhoneDigits(address.phone || ''), // Store digits only server-side
  };
}

/**
 * Check if address is complete (all required fields filled)
 */
export function isAddressComplete(address) {
  const requiredFields = ['first_name', 'last_name', 'address_line1', 'city', 'state', 'zip', 'phone'];
  return requiredFields.every(field => {
    const value = address[field];
    return value && normalizeText(value).length > 0;
  });
}

export default {
  AddressRules,
  validateField,
  validateAddress,
  normalizeAddress,
  normalizeText,
  formatZip,
  formatPhone,
  extractPhoneDigits,
  titleCaseCity,
  isAddressComplete,
};
